/**
 * ページサービス
 *
 * Firestoreでのページデータの CRUD 操作を提供
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

// ページコレクションへの参照
const pagesCollection = collection(db, 'pages');

/**
 * ページを保存
 * @param {Object} pageData - ページデータ
 * @param {string} userId - ユーザーID
 * @returns {Promise<Object>} 保存されたページ
 */
export async function savePage(pageData, userId) {
  console.log('[KnoVelo Pages] ページを保存中...');

  const docData = {
    ...pageData,
    userId,
    pageId: '', // 後でドキュメントIDを設定
    createdAt: serverTimestamp(),
    accessedAt: serverTimestamp(),
    isDeleted: false
  };

  try {
    const docRef = await addDoc(pagesCollection, docData);

    // pageIdをドキュメントIDで更新
    await updateDoc(docRef, { pageId: docRef.id });

    console.log('[KnoVelo Pages] ページを保存しました:', docRef.id);

    return {
      ...docData,
      pageId: docRef.id
    };
  } catch (error) {
    console.error('[KnoVelo Pages] ページ保存エラー:', error);
    throw error;
  }
}

/**
 * ページを取得
 * @param {string} pageId - ページID
 * @returns {Promise<Object|null>} ページデータ
 */
export async function getPage(pageId) {
  console.log('[KnoVelo Pages] ページを取得中:', pageId);

  try {
    const docRef = doc(db, 'pages', pageId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        pageId: docSnap.id
      };
    }

    return null;
  } catch (error) {
    console.error('[KnoVelo Pages] ページ取得エラー:', error);
    throw error;
  }
}

/**
 * ユーザーのページ一覧を取得
 * @param {string} userId - ユーザーID
 * @param {Object} options - オプション
 * @returns {Promise<Object[]>} ページ配列
 */
export async function getPages(userId, options = {}) {
  console.log('[KnoVelo Pages] ページ一覧を取得中...');

  const {
    limitCount = 50,
    sortBy = 'createdAt',
    sortOrder = 'desc',
    tag = null
  } = options;

  try {
    let q = query(
      pagesCollection,
      where('userId', '==', userId),
      where('isDeleted', '==', false),
      orderBy(sortBy, sortOrder),
      limit(limitCount)
    );

    // タグでフィルター（オプション）
    if (tag) {
      q = query(
        pagesCollection,
        where('userId', '==', userId),
        where('isDeleted', '==', false),
        where('autoTags', 'array-contains', tag),
        orderBy(sortBy, sortOrder),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(q);
    const pages = [];

    querySnapshot.forEach((doc) => {
      pages.push({
        ...doc.data(),
        pageId: doc.id
      });
    });

    console.log('[KnoVelo Pages] ページ一覧を取得しました:', pages.length, '件');
    return pages;
  } catch (error) {
    console.error('[KnoVelo Pages] ページ一覧取得エラー:', error);
    throw error;
  }
}

/**
 * 最近のページを取得
 * @param {string} userId - ユーザーID
 * @param {number} count - 取得件数
 * @returns {Promise<Object[]>} ページ配列
 */
export async function getRecentPages(userId, count = 5) {
  return getPages(userId, {
    limitCount: count,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
}

/**
 * ページを更新
 * @param {string} pageId - ページID
 * @param {Object} updateData - 更新データ
 * @returns {Promise<void>}
 */
export async function updatePage(pageId, updateData) {
  console.log('[KnoVelo Pages] ページを更新中:', pageId);

  try {
    const docRef = doc(db, 'pages', pageId);
    await updateDoc(docRef, {
      ...updateData,
      accessedAt: serverTimestamp()
    });

    console.log('[KnoVelo Pages] ページを更新しました');
  } catch (error) {
    console.error('[KnoVelo Pages] ページ更新エラー:', error);
    throw error;
  }
}

/**
 * ページを削除（論理削除）
 * @param {string} pageId - ページID
 * @returns {Promise<void>}
 */
export async function deletePage(pageId) {
  console.log('[KnoVelo Pages] ページを削除中:', pageId);

  try {
    const docRef = doc(db, 'pages', pageId);
    await updateDoc(docRef, {
      isDeleted: true,
      deletedAt: serverTimestamp()
    });

    console.log('[KnoVelo Pages] ページを削除しました');
  } catch (error) {
    console.error('[KnoVelo Pages] ページ削除エラー:', error);
    throw error;
  }
}

/**
 * ページを完全削除（物理削除）
 * @param {string} pageId - ページID
 * @returns {Promise<void>}
 */
export async function permanentlyDeletePage(pageId) {
  console.log('[KnoVelo Pages] ページを完全削除中:', pageId);

  try {
    const docRef = doc(db, 'pages', pageId);
    await deleteDoc(docRef);

    console.log('[KnoVelo Pages] ページを完全削除しました');
  } catch (error) {
    console.error('[KnoVelo Pages] ページ完全削除エラー:', error);
    throw error;
  }
}

/**
 * ページ数を取得
 * @param {string} userId - ユーザーID
 * @returns {Promise<number>} ページ数
 */
export async function getPageCount(userId) {
  try {
    const q = query(
      pagesCollection,
      where('userId', '==', userId),
      where('isDeleted', '==', false)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('[KnoVelo Pages] ページ数取得エラー:', error);
    throw error;
  }
}

/**
 * タグでページを検索
 * @param {string} userId - ユーザーID
 * @param {string} tag - タグ
 * @returns {Promise<Object[]>} ページ配列
 */
export async function getPagesByTag(userId, tag) {
  return getPages(userId, { tag });
}
